import { z } from 'zod';
import type { ProblemId } from './problems';
import { withoutEmDashes } from './plain-text';

// The paid 14-day plan for the reader's named problem. Written by one generateObject
// pass (OpenAI strict mode): every field required, no format validators, no optional.

export const PROBLEM_FINDER_PLAN_TASK_ID = 'problem-finder-plan-generator';
export const PROBLEM_FINDER_PLAN_PRICE_USD = 19;
export const PROBLEM_FINDER_PLAN_PRODUCT = 'problem-finder-plan';
export const PLAN_DAYS = 14;
export const CHECKPOINT_DAYS = [7, 14] as const;
export const PLAN_PATHS = 3;
export const PLAN_WEEKS = [1, 2] as const;

export const PlanDaySchema = z.object({
  day: z.number().describe('Day number, 1 to 14.'),
  title: z.string().describe('A short name for the day, 2 to 6 words.'),
  action: z
    .string()
    .describe('The one thing to do today, one or two sentences, concrete enough to finish.'),
  how: z
    .string()
    .describe(
      'Exactly how to do it: 2 to 5 short numbered steps, or a script or fill-in template to copy, one item per line.'
    ),
  doneWhen: z.string().describe('One sentence: what exists or has happened once today is done.'),
  badDayVersion: z
    .string()
    .describe('The 5-minute version of today for a bad day, one sentence. Still moves the plan forward.'),
  why: z.string().describe('One sentence on why this step comes today and what it sets up for tomorrow.'),
  minutes: z.number().describe('Roughly how many minutes it takes.'),
});

export const PlanWeekSchema = z.object({
  week: z.number().describe('1 or 2.'),
  goal: z.string().describe('What this week is for, one sentence.'),
  endsWith: z.string().describe('What they will have in hand at the end of the week, one sentence.'),
});

export const PlanToolSchema = z.object({
  name: z.string().describe('A short name for the tool, 2 to 5 words.'),
  purpose: z.string().describe('One sentence: what it is for and which days use it.'),
  content: z
    .string()
    .describe(
      'The tool itself, ready to copy and fill in: headings, prompts, blanks or script lines, one item per line. 8 to 20 lines, filled with their situation where their answers allow.'
    ),
});

export const PlanCheckpointSchema = z.object({
  day: z.number().describe('7 or 14.'),
  question: z.string().describe('The one question to answer honestly at this checkpoint.'),
  ifYes: z.string().describe('What to do next if the answer is yes.'),
  ifNo: z.string().describe('What to change if the answer is no. Never "try harder".'),
});

export const PlanPathSchema = z.object({
  name: z.string().describe('A short name for this way through the problem.'),
  whyItFits: z.string().describe('One or two sentences tying it to the reader\'s answers.'),
  smallTest: z.string().describe('A test they can run in under a week to see if it fits.'),
});

export const ProblemPlanSchema = z.object({
  problemStatement: z.string().describe('The named problem, one sentence, second person.'),
  outcome: z
    .string()
    .describe('What they will have by day 14 if they follow the plan, one or two concrete sentences.'),
  whatKeepsItStuck: z
    .string()
    .describe('Two short paragraphs separated by a blank line: what has kept this stuck and how the plan gets around it.'),
  paths: z
    .array(PlanPathSchema)
    .describe('Exactly three different ways through this problem that fit the reader.'),
  weeks: z.array(PlanWeekSchema).describe('Exactly two weeks: week 1 (days 1 to 7) and week 2 (days 8 to 14).'),
  days: z.array(PlanDaySchema).describe('Exactly 14 days, in order, day 1 to day 14.'),
  tool: PlanToolSchema.describe('The one working tool the plan uses, handed over ready to fill in.'),
  checkpoints: z.array(PlanCheckpointSchema).describe('Exactly two checkpoints: day 7 and day 14.'),
  whenItFallsApart: z
    .array(z.string())
    .describe('Three short moves for the week the plan falls apart, each one sentence.'),
});

export type PlanDay = z.infer<typeof PlanDaySchema>;
export type PlanCheckpoint = z.infer<typeof PlanCheckpointSchema>;
export type PlanPath = z.infer<typeof PlanPathSchema>;
export type PlanWeek = z.infer<typeof PlanWeekSchema>;
export type PlanTool = z.infer<typeof PlanToolSchema>;
export type ProblemPlanContent = z.infer<typeof ProblemPlanSchema>;

/** What `visitor_reports/{runId}.report` holds for a Problem Finder run. */
export interface ProblemPlan extends ProblemPlanContent {
  problemId: ProblemId;
  planName: string;
}

// The page prints "By day 14:" before the outcome; the model tends to open with it too.
function withoutDayLabel(outcome: string): string {
  const rest = outcome.trim().replace(/^by day \d+[,:]?\s*/i, '');
  return rest.charAt(0).toUpperCase() + rest.slice(1);
}

/**
 * Orders the days, checkpoints and weeks, and rejects a plan that does not cover days 1
 * to 14 (each with its how-to), both checkpoints, both weeks, three paths, a filled tool
 * and at least one fall-apart move. The page links each day in sequence, so a gap would
 * read as a broken plan; throwing lets Trigger.dev retry the generation instead.
 */
export function normalizePlanContent(content: ProblemPlanContent): ProblemPlanContent {
  const days = [...content.days].sort((a, b) => a.day - b.day);
  const dayNumbers = days.map((d) => d.day);
  const expectedDays = Array.from({ length: PLAN_DAYS }, (_, i) => i + 1);
  if (
    dayNumbers.length !== expectedDays.length ||
    expectedDays.some((day, i) => dayNumbers[i] !== day)
  ) {
    throw new Error(`Plan did not cover days 1 to ${PLAN_DAYS}. Got: ${dayNumbers.join(', ') || 'none'}`);
  }

  const checkpoints = [...content.checkpoints].sort((a, b) => a.day - b.day);
  const checkpointDays = checkpoints.map((c) => c.day);
  if (
    checkpointDays.length !== CHECKPOINT_DAYS.length ||
    CHECKPOINT_DAYS.some((day, i) => checkpointDays[i] !== day)
  ) {
    throw new Error(`Plan checkpoints must be days 7 and 14. Got: ${checkpointDays.join(', ') || 'none'}`);
  }

  // The page and every offer promise three ways through; strict mode does not enforce
  // a count written in a description.
  if (content.paths.length !== PLAN_PATHS) {
    throw new Error(`Plan must have ${PLAN_PATHS} paths. Got: ${content.paths.length}`);
  }

  if (!content.whenItFallsApart.some((move) => move.trim())) {
    throw new Error('Plan has no moves for the week it falls apart');
  }

  const weeks = [...content.weeks].sort((a, b) => a.week - b.week);
  const weekNumbers = weeks.map((w) => w.week);
  if (
    weekNumbers.length !== PLAN_WEEKS.length ||
    PLAN_WEEKS.some((week, i) => weekNumbers[i] !== week)
  ) {
    throw new Error(`Plan weeks must be 1 and 2. Got: ${weekNumbers.join(', ') || 'none'}`);
  }

  // The page renders these as the day's how-to and the handed-over tool; empty ones read as missing.
  const thinDays = days.filter((d) => !d.how.trim() || !d.doneWhen.trim() || !d.badDayVersion.trim());
  if (thinDays.length > 0) {
    throw new Error(`Plan days missing how, done-when or bad-day version: ${thinDays.map((d) => d.day).join(', ')}`);
  }
  if (!content.tool.content.trim()) {
    throw new Error('Plan tool has no content');
  }

  return withoutEmDashes({ ...content, outcome: withoutDayLabel(content.outcome), days, checkpoints, weeks });
}
